/**
 * LL Free gift — cart auto-add
 *
 * Reads the JSON config emitted by snippets/ll-free-gift-config.liquid and
 * keeps the cart reconciled with one rule: when the qualifying lines meet the
 * threshold, the gift line (marked `_llFreeGift`) is present at the configured
 * quantity; when they don't, it isn't.
 *
 * Pricing is NOT this script's job. The gift product keeps its normal price
 * and an automatic Buy X Get Y (100%) discount in the admin zeroes it. That
 * split is what makes the feature safe: any failure mode here leaves the gift
 * at full price (or absent), never free-without-qualifying. As a fuse, after
 * adding — or when a later pass finds the gift undiscounted — the gift is
 * removed and the feature stands down for the session, so a missing/broken
 * discount can't surface a paid "gift" to the customer.
 *
 * Qualifying lines exclude the gift itself (also by handle — a full-price
 * copy must not help reach its own threshold), `_llAddon` add-on lines and
 * the embroidery service. Loaded globally (defer) — the cart drawer exists
 * on every page.
 *
 * Session behavior (sessionStorage): a customer who deletes the gift is not
 * fought — it stays gone until the cart drops below the threshold and crosses
 * it again, or a new session starts.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('ll-free-gift-config');
  if (!cfgEl) return;

  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (error) {
    console.warn('[ll-free-gift] invalid config JSON', error);
    return;
  }
  if (!cfg.productHandle || !(cfg.threshold > 0)) return;

  var GIFT_PROP = '_llFreeGift';
  var ADDED_KEY = 'll-free-gift-added';
  var DISMISSED_KEY = 'll-free-gift-dismissed';
  var NO_DISCOUNT_KEY = 'll-free-gift-no-discount';
  var EXCLUDED_HANDLES = ['embroidery-service', cfg.productHandle];

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var busy = false;
  var rerun = false;
  var variantId = null;
  var variantPromise = null;

  function session(key, value) {
    try {
      if (value === undefined) return sessionStorage.getItem(key);
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (error) {
      return null; // private browsing — degrade to no session memory
    }
  }

  function getCart() {
    return fetch(root + 'cart.js').then(function (response) { return response.json(); });
  }

  function post(path, payload) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }

  function refreshUi() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /** First available variant of the gift product, cached for the page. */
  function resolveVariant() {
    if (variantId) return Promise.resolve(variantId);
    if (variantPromise) return variantPromise;
    variantPromise = fetch(root + 'products/' + cfg.productHandle + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (product) {
        var variants = product.variants || [];
        var variant = null;
        if (cfg.variantSku) {
          // Merchant pinned a specific variant — never substitute a sibling.
          variant = variants.filter(function (v) { return v.sku === cfg.variantSku && v.available; })[0];
        } else {
          variant = variants.filter(function (v) { return v.available; })[0];
        }
        variantId = variant ? variant.id : null;
        if (!variantId) console.warn('[ll-free-gift] no available gift variant', cfg.variantSku || '(first available)');
        return variantId;
      })
      .catch(function (error) {
        console.warn('[ll-free-gift] could not resolve gift product:', error);
        variantPromise = null;
        return null;
      });
    return variantPromise;
  }

  function giftLine(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].properties && items[i].properties[GIFT_PROP]) return items[i];
    }
    return null;
  }

  function qualifies(cart) {
    var count = 0;
    var amount = 0;
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      if (props[GIFT_PROP] || props._llAddon) return;
      if (EXCLUDED_HANDLES.indexOf(item.handle) !== -1) return;
      count += item.quantity;
      amount += item.final_line_price; // post line-discount, presentment currency
    });
    return cfg.trigger === 'quantity' ? count >= cfg.threshold : amount >= cfg.threshold;
  }

  /**
   * The gift line must be free. A non-zero final_line_price means the admin
   * discount is missing, mis-scoped, or its quantity doesn't match — remove
   * the gift and stand down for the session rather than show a paid "gift".
   */
  async function blowFuse(line) {
    await post('cart/change.js', { id: line.key, quantity: 0 });
    session(NO_DISCOUNT_KEY, '1');
    console.error(
      '[ll-free-gift] the automatic Buy X Get Y discount did not zero the gift — gift removed.',
      'Check the discount setup (docs/free-gift-config-guide.html).'
    );
    refreshUi();
  }

  async function reconcile() {
    if (busy) {
      rerun = true;
      return;
    }
    busy = true;

    try {
      var cart = await getCart();
      var gift = giftLine(cart);
      var eligible = qualifies(cart);

      if (!eligible) {
        if (gift) {
          await post('cart/change.js', { id: gift.key, quantity: 0 });
          refreshUi();
        }
        // Dropping below the threshold resets the session flags: crossing it
        // again is a fresh offer, even after an earlier dismissal.
        session(ADDED_KEY, null);
        session(DISMISSED_KEY, null);
        return;
      }

      if (gift) {
        session(ADDED_KEY, '1');
        if (gift.quantity !== cfg.quantity) {
          // Clamp: extra units aren't free anyway (discount covers cfg.quantity).
          await post('cart/change.js', { id: gift.key, quantity: cfg.quantity });
          refreshUi();
        } else if (gift.final_line_price !== 0) {
          await blowFuse(gift);
        }
        return;
      }

      if (session(NO_DISCOUNT_KEY) || session(DISMISSED_KEY)) return;
      if (session(ADDED_KEY)) {
        // We added it this session and it's gone while still eligible — the
        // customer deleted it. Respect that.
        session(DISMISSED_KEY, '1');
        return;
      }

      var id = await resolveVariant();
      if (!id) return;

      var properties = {};
      properties[GIFT_PROP] = '1';
      var response = await post('cart/add.js', { id: id, quantity: cfg.quantity, properties: properties });
      if (!response.ok) {
        console.warn('[ll-free-gift] cart/add.js failed:', response.status);
        return;
      }
      session(ADDED_KEY, '1');

      var after = await getCart();
      var added = giftLine(after);
      if (added && added.final_line_price !== 0) {
        await blowFuse(added);
        return;
      }
      refreshUi();
    } catch (error) {
      console.warn('[ll-free-gift] reconcile failed:', error);
    } finally {
      busy = false;
      if (rerun) {
        rerun = false;
        reconcile();
      }
    }
  }

  function init() {
    reconcile();
    // Theme cart churn (drawer quantity changes, add-ons, embroidery all
    // announce one of these). Our own refreshUi() re-enters here too; the
    // busy guard + idempotent reconcile make that converge instead of loop.
    document.addEventListener('cart:refresh', reconcile);
    document.addEventListener('cart:change', reconcile);
    // Backstop for cart mutations that announce nothing.
    setInterval(reconcile, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
